import { useState, useEffect } from "react";
import { Sparkles, X } from "lucide-react";

/**
 * FeatureHint — a one-time, dismissible "new here" nudge shown the first time a
 * user encounters a feature. Remembers dismissal in localStorage (per hint id),
 * so it never nags. Purely additive; renders nothing once seen.
 */
export const FeatureHint = ({ id, title, children, testid, className = "" }) => {
  const key = `erio_hint_${id}`;
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      setShow(localStorage.getItem(key) !== "1");
    } catch {
      setShow(true);
    }
  }, [key]);

  if (!show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      data-testid={testid || `feature-hint-${id}`}
      className={`relative flex items-start gap-3 rounded-xl border border-ek-purple/20 bg-ek-purple/[0.05] p-3.5 pr-9 ${className}`}
    >
      <Sparkles size={16} className="mt-0.5 shrink-0 text-ek-purple" />
      <div className="text-sm text-ek-ink/75">
        {title && <p className="font-semibold text-ek-ink">{title}</p>}
        <div className="mt-0.5 text-xs leading-relaxed text-ek-ink/60">{children}</div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid={`${testid || `feature-hint-${id}`}-dismiss`}
        className="absolute right-2.5 top-2.5 rounded-md p-1 text-ek-ink/35 transition-colors hover:bg-ek-ink/5 hover:text-ek-ink"
      >
        <X size={14} />
      </button>
    </div>
  );
};
